import React, { useEffect, useState } from "react";
import BasedModal, { IProps } from "components/BaseModalComponents/BasedModal";
import {
    Container,
    ModalBody,
    LogTable,
    TabContainer,
    Tab,
    Table,
} from "./styles";
import ModalFooter from "components/BaseModalComponents/Footer/ModalFooter";
import SystemLogItem from "./SystemLogItem";
import ChangeLogItem from "./ChangeLogItem";
import Loader from "components/Loader/Loader";
import LogFilters from "./LogFilters";
import WebAlertLogItem from "./WebAlertsLogItem";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import {
    loadNotifications,
    markAsReadSuccess,
    setFilters,
} from "store/reducers/NotificationSlice";
import { StyledSpin } from "components/Loader/styles";


export interface ChangeLogEntry {
    is_read: boolean;
    source: "changes";
    action_by_id: number;
    id: number;
    action_by: string;
    group_name: string;
    action: string;
    created_at: string;
    ticker: string;
    meta: {
        before: string | null;
        after: string | null;
    };
}

export interface SystemLogEntry {
    source: "system";
    id: string;
    ticker_name: string | null;
    tab_id: number | null;
    created_at: string;
    level: "Info" | "Warning" | "Error";
    module: string;
    text: string;
}
export interface WebAlertLogEntry {
    id: number;
    text: string;
    level: "Info" | "Warning" | "Error";
    created_at: string;
    source: "web_alerts";
}

interface NotificationModalProps extends IProps {}

const NotificationModal: React.FC<NotificationModalProps> = ({
    isOpen,
    onRequestClose,
}) => {
    const dispatch = useAppDispatch();
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState<
        "changes" | "system" | "web_alerts"
    >("changes");
    const selectedRobotId = useAppSelector(
        (state) => state.robots.selectedRobotId,
    );
    const { changes, system, web_alerts, globalLoading, filters } =
        useAppSelector((state) => state.notifications);
    const [page, setPage] = useState({
        changes: 1,
        system: 1,
        web_alerts: 1,
    });

    const onFilterChange = (
        tab: "changes" | "system" | "web_alerts",
        key: string,
        value: string,
    ) => {
        dispatch(
            setFilters({
                source: tab,
                filters: { ...filters[tab], [key]: value },
            }),
        );

        // const methods = (window as any).notificationSocketMethods;
        // if (methods?.loadMore) {
        //     methods.loadMore(tab, 1);
        // }
    };

    const resetFilters = (tab: "changes" | "system" | "web_alerts") => {
        let defaultFilters: any;

        if (tab === "changes") {
            defaultFilters = {
                robot_id: selectedRobotId,
                group_name: "",
                action: "",
                action_by_id: "",
                ticker: "",
            };
        } else if (tab === "system") {
            defaultFilters = {
                level: "",
                from_date: "",
                to_date: "",
                linked: undefined,
            };
        } else if (tab === "web_alerts") {
            defaultFilters = {
                robot_id: selectedRobotId,
                level: "",
            };
        }

        dispatch(setFilters({ source: tab, filters: defaultFilters }));

        const methods = (window as any).notificationSocketMethods;
        if (methods?.loadMore) {
            methods.loadMore(tab, 1);
        }

        setPage((prev) => ({ ...prev, [tab]: 1 }));
    };
    const toggleExpand = (index: number) => {
        setExpandedIndex((prev) => (prev === index ? null : index));
    };

    const formatDateTime = (dateStr: string) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${day}.${month}.${year} ${hours}:${minutes}`;
    };
    const logs: Record<"changes" | "system" | "web_alerts", any> = {
        changes: changes.logs,
        system: system.logs,
        web_alerts: web_alerts.logs,
    };
    const currentLogs = logs[activeTab];

    const pages: Record<"changes" | "system" | "web_alerts", number> = {
        changes: page.changes,
        system: page.system,
        web_alerts: page.web_alerts,
    };
    const currentPage = pages[activeTab];
    const total: Record<"changes" | "system" | "web_alerts", number> = {
        changes: changes.lastPage,
        system: system.lastPage,
        web_alerts: web_alerts.lastPage,
    };
    const totalPages = total[activeTab];

    const goToPage = (newPage: number) => {
        if (newPage < 1 || newPage > totalPages) return;

        setPage((prev) => ({
            ...prev,
            [activeTab]: newPage,
        }));

        const methods = (window as any).notificationSocketMethods;
        if (methods?.loadMore) {
            const currentFilters = filters[activeTab];
            methods.loadMore(activeTab, newPage, currentFilters);
        } else {
            console.error("Socket methods not available");
        }

        setExpandedIndex(null);
    };
    const nextPage = () => goToPage(currentPage + 1);
    const prevPage = () => goToPage(currentPage - 1);
    const renderPageButtons = () => {
        const buttons = [];
        const start = Math.max(1, currentPage - 2);
        const end = Math.min(totalPages, start + 4);

        if (start > 1) {
            buttons.push(
                <button
                    key="1"
                    onClick={() => goToPage(1)}
                    disabled={currentPage === 1 || globalLoading}
                    style={{
                        cursor: globalLoading ? "not-allowed" : "pointer",
                    }}
                >
                    1
                </button>,
            );
            if (start > 2) buttons.push(<span key="start-ellipsis">…</span>);
        }

        for (let i = start; i <= end; i++) {
            buttons.push(
                <button
                    key={i}
                    onClick={() => goToPage(i)}
                    disabled={globalLoading}
                    style={{
                        fontWeight: i === currentPage ? "bold" : "normal",
                        backgroundColor:
                            i === currentPage ? "#7CBE7B" : "white",
                        color: i === currentPage ? "white" : "black",
                        border: "1px solid #ddd",
                        margin: "0 2px",
                        padding: "5px 4px",
                        cursor: globalLoading ? "not-allowed" : "pointer",
                    }}
                >
                    {i}
                </button>,
            );
        }

        if (end < totalPages) {
            if (end < totalPages - 1)
                buttons.push(<span key="end-ellipsis">…</span>);
            buttons.push(
                <button
                    key={totalPages}
                    onClick={() => goToPage(totalPages)}
                    style={{
                        cursor: globalLoading ? "not-allowed" : "pointer",
                    }}
                >
                    {totalPages}
                </button>,
            );
        }

        return buttons;
    };
    const handleTabChange = (tab: "changes" | "system" | "web_alerts") => {
        setActiveTab(tab);
        setExpandedIndex(null);
        const methods = (window as any).notificationSocketMethods;

        if (tab === "system") {
            methods?.markSystemRead?.();
        }
        if (tab === "changes") {
            methods?.markChangesRead?.();
        }
        if (tab === "web_alerts") {
            methods?.markWebAlertsRead?.();
        }
    };

    return (
        <BasedModal isOpen={isOpen} onRequestClose={onRequestClose}>
            <Container
                style={{
                    width: "700px",
                    height: "660px",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <ModalBody
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        overflow: "hidden",
                    }}
                >
                    <h3>
                        Журнал действий{" "}
                        {activeTab === "system" && `для ${selectedRobotId}`}
                    </h3>

                    <TabContainer>
                        <Tab
                            $isActive={activeTab === "changes"}
                            onClick={() => handleTabChange("changes")}
                        >
                            Изменения ({changes.unread})
                        </Tab>
                        <Tab
                            $isActive={activeTab === "system"}
                            onClick={() => handleTabChange("system")}
                        >
                            Системные ({system.unread})
                        </Tab>
                        <Tab
                            $isActive={activeTab === "web_alerts"}
                            onClick={() => handleTabChange("web_alerts")}
                        >
                            Веб алертс ({web_alerts.unread})
                        </Tab>
                    </TabContainer>
                    <LogFilters
                        activeTab={activeTab}
                        setPage={setPage}
                        onFilterChange={onFilterChange}
                        filters={filters}
                        onResetFilters={resetFilters}
                    />

                    {currentLogs.length === 0 && !globalLoading ? (
                        <p>Нет записей.</p>
                    ) : (
                        <div
                            style={{
                                flex: 1,
                                overflowY: "auto",
                                minHeight: 0,
                                marginBottom: "12px",
                                opacity: globalLoading ? 0.5 : 1,
                                transition: "opacity 0.3s ease",
                            }}
                        >
                            {!globalLoading ? (
                                <Table>
                                    <LogTable>
                                        {currentLogs
                                            .slice(0, 10)
                                            .map((log, index) => (
                                                <React.Fragment
                                                    key={`${log.source}-${log.id}-${log.created_at}`}
                                                >
                                                    {log.source ===
                                                        "changes" && (
                                                        <ChangeLogItem
                                                            log={
                                                                log as ChangeLogEntry
                                                            }
                                                            isAllChangesUnread={
                                                                changes.unread !==
                                                                0
                                                            }
                                                            isExpanded={
                                                                expandedIndex ===
                                                                index
                                                            }
                                                            onToggle={() =>
                                                                toggleExpand(
                                                                    index,
                                                                )
                                                            }
                                                            formatDateTime={
                                                                formatDateTime
                                                            }
                                                            onRead={(
                                                                id: number,
                                                            ) => {
                                                                dispatch(
                                                                    markAsReadSuccess(
                                                                        {
                                                                            source: "changes",
                                                                            id,
                                                                        },
                                                                    ),
                                                                );
                                                                const methods =
                                                                    (
                                                                        window as any
                                                                    )
                                                                        .notificationSocketMethods;
                                                                if (
                                                                    methods?.markAsRead
                                                                ) {
                                                                    methods.markAsRead(
                                                                        id,
                                                                    );
                                                                } else {
                                                                    console.warn(
                                                                        "markAsRead method not available",
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                    {log.source ===
                                                        "system" && (
                                                        <SystemLogItem
                                                            log={
                                                                log as SystemLogEntry
                                                            }
                                                            isExpanded={
                                                                expandedIndex ===
                                                                index
                                                            }
                                                            onToggle={() =>
                                                                toggleExpand(
                                                                    index,
                                                                )
                                                            }
                                                            formatDateTime={
                                                                formatDateTime
                                                            }
                                                            onRequestClose={
                                                                onRequestClose
                                                            }
                                                            isUnreadSystemLogs={
                                                                system.unread >
                                                                2
                                                                    ? true
                                                                    : false
                                                            }
                                                        />
                                                    )}
                                                    {log.source ===
                                                        "web_alerts" && (
                                                        <WebAlertLogItem
                                                            log={
                                                                log as WebAlertLogEntry
                                                            }
                                                            onToggle={() =>
                                                                toggleExpand(
                                                                    index,
                                                                )
                                                            }
                                                            formatDateTime={
                                                                formatDateTime
                                                            }
                                                            isUnreadWebAlertsLogs={
                                                                web_alerts.unread >
                                                                2
                                                                    ? true
                                                                    : false
                                                            }
                                                        />
                                                    )}
                                                </React.Fragment>
                                            ))}
                                    </LogTable>
                                </Table>
                            ) : (
                                <StyledSpin size="large" />
                            )}
                        </div>
                    )}
                    {/* Пагинация */}
                    {totalPages > 1 && (
                        <div
                            style={{
                                marginTop: "auto",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "8px",
                                flexWrap: "wrap",
                            }}
                        >
                            <button
                                onClick={prevPage}
                                disabled={currentPage === 1 || globalLoading}
                                style={{
                                    padding: "5px 10px",
                                    cursor:
                                        currentPage === 1 || globalLoading
                                            ? "not-allowed"
                                            : "pointer",
                                    opacity: currentPage === 1 ? 0.5 : 1,
                                }}
                            >
                                {"<"}
                            </button>

                            {renderPageButtons()}

                            <button
                                onClick={nextPage}
                                disabled={
                                    currentPage === totalPages || globalLoading
                                }
                                style={{
                                    padding: "5px 10px",
                                    cursor:
                                        currentPage === totalPages ||
                                        globalLoading
                                            ? "not-allowed"
                                            : "pointer",
                                    opacity:
                                        currentPage === totalPages ? 0.5 : 1,
                                }}
                            >
                                {">"}
                            </button>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter
                    applyText="ОК"
                    onApplyClick={onRequestClose}
                    cancelText="Закрыть"
                    onCancelClick={onRequestClose}
                />
            </Container>
        </BasedModal>
    );
};

export default NotificationModal;
